import React, { useEffect, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

const Wrapper = styled.div`
  position: relative;
  padding: 1rem;
`;

const ScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: 1rem;
`;

const Table = styled.table`
  width: 100%;
  height: 70vh;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  font-size: 0.7rem;

  th,
  td {
    border: 1px solid #ddd;
    padding: 6px;
    text-align: center;
  }

  th {
    background: #87ceeb;
  }

  .up {
    color: green;
    font-weight: bold;
  }
  .down {
    color: red;
    font-weight: bold;
  }
  .neutral {
    color: black;
  }

  .top-rank {
    color: green;
    font-weight: bold;
  }
  .low-rank {
    color: red;
    font-weight: bold;
  }
  .mid-rank {
    color: gray;
    font-weight: 500;
  }

  .up-bg {
    background-color: rgba(0, 128, 0, 0.1);
    color: green;
    font-weight: bold;
  }
  .down-bg {
    background-color: rgba(255, 0, 0, 0.1);
    color: red;
    font-weight: bold;
  }
  .neutral-bg {
    background-color: #f5f5f5;
    color: black;
  }
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  text-align: center;
  color: #666;
  margin-bottom: 12px;
`;

const monthNamesPL = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

// ✅ Wspólna funkcja – uwzględnia wartości ujemne (korekty)
const getSalesValue = (row) => {
  const raw =
    row.K_15 ??
    row.K_19 ??
    row.K_10 ??
    row.K_21 ??
    row.K_17 ??
    row.K_11 ??
    row.K_22 ??
    0;

  const value =
    typeof raw === "string"
      ? parseFloat(raw.replace(",", "."))
      : Number(raw);

  return Number.isFinite(value) ? value : 0; // może być ujemne
};

const TabelaPorownaniaMiesiecy = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);

  const getRankClass = (rank, total) => {
    if (rank === null) return "mid-rank";
    if (rank <= 3) return "top-rank";
    if (rank >= total - 2) return "low-rank";
    return "mid-rank";
  };

  // ✅ Teraz używamy funkcji, która nie ucina minusów
  const calculateSales = (row) => getSalesValue(row);

  useEffect(() => {
    const years = new Set();
    data.forEach((row) => {
      const year = parseInt(row.Rok, 10);
      if (!isNaN(year)) years.add(year);
    });
    const sortedYears = Array.from(years).sort((a, b) => a - b);
    setAvailableYears(sortedYears);
    if (!selectedYear && sortedYears.length > 0) {
      setSelectedYear(sortedYears[sortedYears.length - 1]);
    }
  }, [data]); // eslint-disable-line

  useEffect(() => {
    if (!selectedYear) return;

    const grouped = {};
    data.forEach((row) => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      if (isNaN(year) || isNaN(month)) return;

      if (!grouped[year]) grouped[year] = {};
      if (!grouped[year][month]) grouped[year][month] = 0;
      grouped[year][month] += calculateSales(row);
    });

    const currentYearData = [];
    const prevYearData = [];

    for (let month = 1; month <= 12; month++) {
      const current = grouped[selectedYear]?.[month] || 0;
      const prevMonth =
        month === 1
          ? grouped[selectedYear - 1]?.[12] ?? null
          : grouped[selectedYear]?.[month - 1] ?? null;
      const prevYear = grouped[selectedYear - 1]?.[month] ?? null;

      // Zostawiam logikę "tylko gdy bazowa > 0", żeby nie robić dzikich % przy 0/ujemnych
      const mmChange =
        prevMonth !== null && prevMonth > 0
          ? ((current - prevMonth) / prevMonth) * 100
          : null;

      const yyChange =
        prevYear !== null && prevYear > 0
          ? ((current - prevYear) / prevYear) * 100
          : null;

      currentYearData.push({
        month,
        monthName: monthNamesPL[month - 1],
        current,
        mmChange,
        yyChange,
      });

      prevYearData.push({ month, value: prevYear || 0 });
    }

    const currentRanked = [...currentYearData]
      .sort((a, b) => b.current - a.current)
      .map((item, index) => ({ ...item, rank: index + 1 }));

    const prevRanked = [...prevYearData]
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({ month: item.month, prevRank: index + 1 }));

    const result = currentYearData.map((item) => {
      const currentRank =
        currentRanked.find((r) => r.month === item.month)?.rank ?? null;
      const prevRank =
        prevRanked.find((r) => r.month === item.month)?.prevRank ?? null;
      const rankChange =
        currentRank && prevRank ? prevRank - currentRank : null;

      const prevYearValue =
        prevYearData.find((m) => m.month === item.month)?.value || 0;
      const prevMonthValue =
        item.month === 1
          ? grouped[selectedYear - 1]?.[12] || 0
          : grouped[selectedYear]?.[item.month - 1] || 0;

      return {
        ...item,
        rank: currentRank,
        prevRank,
        rankChange,
        prevYearValue,
        prevMonthValue,
      };
    });

    setMonthlyStats(result);
  }, [selectedYear, data]);

  const getPercentClass = (value) => {
    if (value === null) return "neutral-bg";
    if (value > 0) return "up-bg";
    if (value < 0) return "down-bg";
    return "neutral-bg";
  };

  const formatPercentText = (value) => {
    if (value === null || !Number.isFinite(value)) return "–";
    return `${Math.abs(value).toFixed(1)}%`;
  };

  const exportToExcel = () => {
    const exportData = monthlyStats.map((item) => ({
      Miesiąc: item.monthName,
      "Wartość sprzedaży (PLN)": item.current.toFixed(2),
      "Wartość m-1 (PLN)": item.prevMonthValue.toFixed(2),
      "Wartość r-1 (PLN)": item.prevYearValue.toFixed(2),
      "Zmiana m/m (%)":
        item.mmChange !== null && Number.isFinite(item.mmChange)
          ? Math.abs(item.mmChange).toFixed(1)
          : "–",
      "Zmiana r/r (%)":
        item.yyChange !== null && Number.isFinite(item.yyChange)
          ? Math.abs(item.yyChange).toFixed(1)
          : "–",
      Ranking: item.rank ?? "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      `Sprzedaż ${selectedYear}`
    );
    XLSX.writeFile(workbook, `Sprzedaz_miesieczna_${selectedYear}.xlsx`);
  };

  return (
    <Wrapper>
      <ChartTitle>
        Przegląd miesięcy w roku {selectedYear}
        <InfoIconWrapper data-tooltip-id="monthly-summary-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="monthly-summary-tooltip"
        place="right"
        content={
          <>
            <strong>Zmiany miesięczne</strong>
            <br />
            Tabela przedstawia wartości sprzedaży
            i zmiany procentowe m/m oraz r/r dla każdego miesiąca {selectedYear}.
          </>
        }
      />

      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Rok:</label>
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={exportToExcel}
          style={{
            backgroundColor: "#1D6F42",
            color: "white",
            padding: "8px 16px",
            border: "none",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          <FaFileExcel size={18} style={{ marginRight: "8px" }} />
          Eksportuj do Excela
        </button>
      </div>

      <ScrollWrapper>
        <Table>
          <thead>
            <tr>
              <th>Miesiąc</th>
              <th>Wartość sprzedaży</th>
              <th>Wartość m-1</th>
              <th>Wartość r-1</th>
              <th>Zmiana m/m</th>
              <th>Zmiana r/r</th>
              <th>Ranking</th>
              <th>Ranking -1r</th>
              <th>Zmiana rankingu r/r</th>
            </tr>
          </thead>
          <tbody>
            {monthlyStats.map((item, idx) => (
              <tr key={idx}>
                <td>{item.monthName}</td>
                <td>
                  {item.current.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                  })}
                </td>
                <td>
                  {item.prevMonthValue.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                  })}
                </td>
                <td>
                  {item.prevYearValue.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                  })}
                </td>
                <td className={getPercentClass(item.mmChange)}>
                  {formatPercentText(item.mmChange)}
                </td>
                <td className={getPercentClass(item.yyChange)}>
                  {formatPercentText(item.yyChange)}
                </td>
                <td className={getRankClass(item.rank, monthlyStats.length)}>
                  {item.rank !== null ? item.rank : "-"}
                </td>
                <td className={getRankClass(item.prevRank, monthlyStats.length)}>
                  {item.prevRank !== null ? item.prevRank : "-"}
                </td>
                <td>
                  {item.rankChange !== null ? (
                    item.rankChange > 0 ? (
                      <span className="up">↑ {item.rankChange}</span>
                    ) : item.rankChange < 0 ? (
                      <span className="down">↓ {Math.abs(item.rankChange)}</span>
                    ) : (
                      <span className="neutral">–</span>
                    )
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScrollWrapper>
    </Wrapper>
  );
};

export default TabelaPorownaniaMiesiecy;
